import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteFaqExample, updateFaqExample } from '../../../api/faq'
import { ReactComponent as PlusCircle } from '../../../assets/icons/add-circle.svg'
import { ReactComponent as CheckmarkCircle } from '../../../assets/icons/checkmark-circle.svg'
import { ReactComponent as Close } from '../../../assets/icons/close.svg'
import { ReactComponent as Pencil } from '../../../assets/icons/pencil.svg'
import { ReactComponent as Thrash } from '../../../assets/icons/trash.svg'
import { IFaqExamplesItem } from '../faqModels'

import NewExampleForm from './FaqNewExample/FaqNewExample'

import styles from './faq-example.module.scss'

interface IFaqExamplesSectionProps {
    data: IFaqExamplesItem[]
    selectedFaqItem: string
    getDataFromApi: () => void
}

function FaqExamplesSection(props: IFaqExamplesSectionProps) {
    const { data, selectedFaqItem, getDataFromApi } = props

    const { t } = useTranslation()

    const [editingExampleText, setEditingExampleText] = useState<string>('')
    const [editingExampleId, setEditingExampleId] = useState<string | null>(
        null
    )
    const [addingExample, setAddingExample] = useState(false)

    const handleSaveEditedExample = async (exampleId: string) => {
        const confirmUpdate = confirm(t('save_example_confirm'))
        if (!confirmUpdate) return

        try {
            await updateFaqExample(
                selectedFaqItem,
                exampleId,
                editingExampleText
            )
            getDataFromApi()
            setEditingExampleId(null)
            setEditingExampleText('')
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const handleEditExample = (exampleId: string, text: string) => {
        setEditingExampleId(exampleId)
        setEditingExampleText(text)
    }

    const handleDeleteExample = async (exampleId: string) => {
        const confirmDelete = confirm(t('delete_example_confirm'))
        if (!confirmDelete) return

        try {
            await deleteFaqExample(selectedFaqItem, exampleId)
            getDataFromApi()
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const handleFormSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (editingExampleId) {
            handleSaveEditedExample(editingExampleId)
        }
    }

    const setSaveEditButtonActive = () => !editingExampleText.trim()

    return (
        <div className={styles.faqExample}>
            <div className={styles.faqExample__header}>
                <h2>{t('questions')}</h2>

                <div>
                    {!addingExample && (
                        <button
                            className={styles.faqExample__btn}
                            onClick={() => setAddingExample(true)}
                        >
                            <PlusCircle />
                            {t('add_new')}
                        </button>
                    )}
                </div>
            </div>

            {addingExample && (
                <NewExampleForm
                    selectedFaqItem={selectedFaqItem}
                    getDataFromApi={getDataFromApi}
                    onCancel={() => setAddingExample(false)}
                />
            )}
            <div className={styles.faqExample__body}>
                {data &&
                    data.map((item, index) => (
                        <div key={index}>
                            {editingExampleId === item._id ? (
                                <form onSubmit={handleFormSubmit}>
                                    <div className={styles.faqExample__row}>
                                        <input
                                            className={
                                                styles.faqExample__row__input
                                            }
                                            type="text"
                                            value={editingExampleText}
                                            onChange={(e) =>
                                                setEditingExampleText(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div
                                            className={
                                                styles.faqExample__buttonsWrapper
                                            }
                                        >
                                            <button
                                                type="submit"
                                                className={`${
                                                    styles.faqExample__borderBtn
                                                } ${
                                                    setSaveEditButtonActive()
                                                        ? styles.faqExample__disabledBtn
                                                        : ''
                                                }`}
                                            >
                                                <CheckmarkCircle />
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    styles.faqExample__borderBtn
                                                }
                                                onClick={() =>
                                                    setEditingExampleId(null)
                                                }
                                            >
                                                <Close />
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            ) : (
                                <div className={styles.faqExample__row}>
                                    <div
                                        className={styles.faqExample__row__text}
                                    >
                                        {item.text}
                                    </div>
                                    <div
                                        className={
                                            styles.faqExample__row__buttons
                                        }
                                    >
                                        <button
                                            className={
                                                styles.faqExample__borderBtn
                                            }
                                            onClick={() =>
                                                handleEditExample(
                                                    item._id,
                                                    item.text
                                                )
                                            }
                                        >
                                            <Pencil />
                                        </button>
                                        <button
                                            className={
                                                styles.faqExample__borderBtn
                                            }
                                            onClick={() =>
                                                handleDeleteExample(item._id)
                                            }
                                        >
                                            <Thrash />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
            </div>
        </div>
    )
}

export default FaqExamplesSection
